import { ALL_CALCULATIONS } from "@suse-o11y/calculate-value-from-points";
import { isTruthy } from "@suse-o11y/is-truthy";
import type { Optional } from "@suse-o11y/optional";
import { z } from "zod";

import type { StatChart as OpenApiStatChart } from "~/main/common/openapi";

import { DEFAULT as DEFAULT_SETTINGS, type Settings } from "../../../components/widget-form/stat-chart/settings";
import { dashboardWidgetFormatSchema } from "../_dashboard-widget-format";

import { ThresholdsSteps } from "./_sub-schemas";

const schema = z
	.object({
		calculation: z.enum(ALL_CALCULATIONS).optional().catch(undefined),

		format: dashboardWidgetFormatSchema.optional().catch(undefined),

		sparkline: z.unknown().transform(isTruthy).optional().catch(undefined),

		metricLabel: z.string().trim().optional().catch(undefined),

		valueFontSize: z.number().optional().catch(undefined),

		thresholds: z
			.object({
				defaultColor: z
					.string()
					.trim()
					.optional()
					.catch(undefined)
					.transform((color) => color || DEFAULT_SETTINGS.color),

				steps: ThresholdsSteps.schema,
			})
			.optional()
			.catch(undefined),
	})
	.catch({});

type StatChart = z.infer<typeof schema>;

function fromSettings(settings: undefined | Settings): StatChart {
	return {
		calculation: settings?.calculation,
		metricLabel: settings?.metricLabel,
		sparkline: settings?.sparkline,
		valueFontSize: settings?.fontSize,

		format: {
			unit: settings?.unit,
			decimalPlaces: settings?.decimals,
		},

		thresholds: {
			defaultColor: settings?.color ?? DEFAULT_SETTINGS.color,
			steps: ThresholdsSteps.fromSettings(settings?.thresholds),
		},
	};
}

function toSettings(spec: undefined | StatChart): Settings {
	return {
		unit: spec?.format?.unit,
		decimals: spec?.format?.decimalPlaces,
		calculation: spec?.calculation,
		sparkline: spec?.sparkline,
		metricLabel: spec?.metricLabel,
		fontSize: spec?.valueFontSize,
		color: spec?.thresholds?.defaultColor,
		thresholds: spec?.thresholds?.steps,
	} satisfies Optional<Settings>;
}

function openApiToSettings(chart: OpenApiStatChart): Settings {
	const { sparkline, calculation, unit, decimals, thresholds: ct } = chart;
	return {
		unit,
		decimals,
		calculation,
		sparkline,
		color: ct?.defaultColor,

		thresholds: ct?.steps.map((step) => ({
			value: step.value,
			color: step.color ?? ct.defaultColor,
		})),
	};
}

const StatChart = { schema, fromSettings, toSettings, openApiToSettings };

export { StatChart };
